<template>
  <!--
    Flat list of <li> (header / link / group) — keeps ALL Vuexy styling and
    behaviour (collapse, accordion, active state), which rely on items being
    direct `.navigation > li` children.

    Re-ordering: only the section title (`.navigation-header`) is a drag handle,
    so links keep navigating. Press & hold a title to drag it — on drop the whole
    section (title + its items, up to the next title) is moved as one block and
    the order is saved to localStorage (per browser / PC).

    Collapse: each section title has a chevron to minimize/expand its group. The
    collapsed set is also saved to localStorage, so it survives refresh / re-login.
  -->
  <draggable
    tag="ul"
    v-model="model"
    handle=".navigation-header"
    filter=".nav-collapse-btn"
    :prevent-on-filter="false"
    :animation="200"
    :fallback-tolerance="4"
    ghost-class="nav-drag-ghost"
    chosen-class="nav-drag-chosen"
    @end="onDragEnd"
  >
    <component
      :is="resolveNavItemComponent(item)"
      v-for="item in model"
      :key="itemKey(item)"
      :item="item"
      v-bind="headerProps(item)"
      v-on="headerListeners(item)"
    />
  </draggable>
</template>

<script>
import { mapGetters } from 'vuex'
import draggable from 'vuedraggable'
import { $themeBreakpoints } from '@themeConfig'
import { resolveVerticalNavMenuItemComponent as resolveNavItemComponent } from '@core/layouts/utils'
import VerticalNavMenuHeader from '../vertical-nav-menu-header/index'
import VerticalNavMenuLink from '../vertical-nav-menu-link/VerticalNavMenuLink.vue'
import VerticalNavMenuGroup from '../vertical-nav-menu-group/VerticalNavMenuGroup.vue'
import mixinRouter from '@/components/mixin/mixinRouter'

const ORDER_KEY = 'crm-menu-section-order'
const COLLAPSE_KEY = 'crm-menu-collapsed-sections'

export default {
  name: 'VerticalNavMenuItems',
  mixins: [mixinRouter],
  components: {
    draggable,
    VerticalNavMenuHeader,
    VerticalNavMenuLink,
    VerticalNavMenuGroup,
  },
  data: () => ({
    resolveNavItemComponent,
    model: [],            // flat list rendered (and mutated while dragging)
    sectionOrder: [],     // persisted section-key order
    collapsedSections: [], // persisted collapsed section keys
  }),
  props: {
    items: {
      type: Array,
      required: true,
    },
  },
  computed: {
    ...mapGetters('user', ['myRole', 'isAdminUser']),
    // Reactive via the store's windowWidth (updated on resize in App.vue) —
    // matches the layout's own desktop/overlay breakpoint.
    isMobileViewport() {
      return this.$store.state.app.windowWidth < $themeBreakpoints.xl
    },
    filteredItems() {
      // mobileOnly items (the "mobile" section) exist solely below the desktop
      // breakpoint — for admins too.
      const base = this.items.filter((item) => !item.mobileOnly || this.isMobileViewport)
      if (this.isAdminUser) return base
      const items = base.filter((item) => this.routerIsAccessible(item.route))
      return items.filter((item, index) => {
        if (item.header != null) {
          return items[index + 1] && items[index + 1].header == null
        }
        if (item.children) {
          item.children = item.children.filter((child) => this.routerIsAccessible(child.route))
          return item.children.length > 0
        }
        return true
      })
    },
    // Group the flat list into [{ key, header, items }] sections.
    sections() {
      const out = []
      let current = null
      this.filteredItems.forEach((it) => {
        if (it.header != null) {
          current = { key: `h:${it.header}`, header: it.header, items: [it] }
          out.push(current)
        } else {
          if (!current) {
            current = { key: '__lead__', header: null, items: [] }
            out.push(current)
          }
          current.items.push(it)
        }
      })
      return out
    },
    // Sections ordered by the persisted order. Lead (header-less) items stay on
    // top; new sections keep their natural position.
    orderedSections() {
      const lead = this.sections.filter((s) => s.header == null)
      const rest = this.sections.filter((s) => s.header != null)
      const map = new Map(rest.map((s) => [s.key, s]))
      const ordered = [];
      (this.sectionOrder || []).forEach((k) => {
        if (map.has(k)) {
          ordered.push(map.get(k))
          map.delete(k)
        }
      })
      rest.forEach((s) => {
        if (map.has(s.key)) ordered.push(s)
      })
      return [...lead, ...ordered]
    },
    // Flatten ordered sections to the flat list, dropping the items of any
    // collapsed section (its header stays visible so it can be re-expanded).
    visibleFlat() {
      const out = []
      this.orderedSections.forEach((s) => {
        if (s.header == null) {
          out.push(...s.items)
          return
        }
        out.push(s.items[0]) // header li always shown
        if (!this.isSectionCollapsed(s.key)) {
          out.push(...s.items.slice(1))
        }
      })
      return out
    },
  },
  watch: {
    visibleFlat: {
      immediate: true,
      handler(v) {
        this.model = v.slice()
      },
    },
  },
  created() {
    this.loadOrder()
    this.loadCollapsed()
  },
  methods: {
    itemKey(item) {
      return item.header ? `header-${item.header}` : `title-${item.title}`
    },
    isSectionCollapsed(key) {
      return (this.collapsedSections || []).includes(key)
    },
    isCollapsed(item) {
      return item.header != null && this.isSectionCollapsed(`h:${item.header}`)
    },
    headerProps(item) {
      if (item.header == null) return {}
      return { collapsible: true, collapsed: this.isCollapsed(item) }
    },
    headerListeners(item) {
      if (item.header == null) return {}
      return { toggle: () => this.toggleSection(item) }
    },
    toggleSection(item) {
      if (item.header == null) return
      const key = `h:${item.header}`
      const i = this.collapsedSections.indexOf(key)
      if (i >= 0) this.collapsedSections.splice(i, 1)
      else this.collapsedSections.push(key)
      this.persistCollapsed()
      // visibleFlat depends on collapsedSections -> watcher rebuilds the model.
    },
    loadOrder() {
      try {
        const raw = localStorage.getItem(ORDER_KEY)
        if (raw) this.sectionOrder = JSON.parse(raw) || []
      } catch (e) {
        this.sectionOrder = []
      }
    },
    persistOrder() {
      try {
        localStorage.setItem(ORDER_KEY, JSON.stringify(this.sectionOrder))
      } catch (e) {
        /* ignore */
      }
    },
    loadCollapsed() {
      try {
        const raw = localStorage.getItem(COLLAPSE_KEY)
        if (raw) this.collapsedSections = JSON.parse(raw) || []
      } catch (e) {
        this.collapsedSections = []
      }
    },
    persistCollapsed() {
      try {
        localStorage.setItem(COLLAPSE_KEY, JSON.stringify(this.collapsedSections))
      } catch (e) {
        /* ignore */
      }
    },
    onDragEnd() {
      // Only headers are draggable, so their order in the (drag-mutated) model is
      // the new section order. Persist it and rebuild clean section blocks.
      this.sectionOrder = this.model
        .filter((i) => i.header != null)
        .map((i) => `h:${i.header}`)
      this.persistOrder()
      this.$nextTick(() => {
        this.model = this.visibleFlat.slice()
      })
    },
  },
}
</script>
